/**
 * BATCH 22 — PART 18: Workflows 214-225
 * CleverTap (214-219) + Braze (220-225)
 * 12 marketing workflow templates, 8 nodes each
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  message: string,
  x = 80,
): any {
  return n(id, "sendSMS", label, y, { sendSmsDetails: { to, message } }, x);
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOW 214-225 ============

export const ELITE_BATCH22_PART18: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 214. Wallet Recharge Reminder | CleverTap | Pattern F ──
  "Wallet Recharge Reminder": {
    nodes: [
      trigger("CleverTap: Wallet balance low event", "webhook_app", {
        source: "clevertap",
        event: "wallet_balance_low",
        howItStarts:
          "Fires when a user's wallet balance drops below their average transaction amount, detected via CleverTap event tracking.",
        setup:
          "Connect CleverTap and configure the 'wallet_balance_low' event to fire when balance < average order value. Include user_id, current_balance, avg_transaction, and last_recharge_date in the event payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract wallet details and recharge urgency",
        280,
        "You are a fintech engagement analyst. Extract wallet and recharge context from the event payload. Return JSON with fields: user_id, current_balance, avg_transaction, days_since_recharge, urgency_level (critical/moderate/low), suggested_recharge_amount, personalized_message.",
        "Wallet event: {webhook_payload}",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "current_balance", as: "current_balance", type: "number" },
          { field: "avg_transaction", as: "avg_transaction", type: "number" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "suggested_recharge_amount",
            as: "suggested_recharge_amount",
            type: "number",
          },
          {
            field: "personalized_message",
            as: "personalized_message",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "CleverTap: Check recharge history and payment methods",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profiles.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "identity", value: "{user_id}" },
              { key: "type", value: "profile" },
            ],
            variables: [
              {
                name: "record.profileData.saved_payment_method",
                customName: "has_saved_payment",
                type: "boolean",
              },
              {
                name: "record.profileData.total_recharges",
                customName: "total_recharges",
                type: "number",
              },
              {
                name: "record.profileData.preferred_recharge_amount",
                customName: "preferred_amount",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Balance critically low?", 760, {
        conditionDetails: {
          conditions: [
            { field: "current_balance", operator: "less_than", value: "50" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "CleverTap: Send urgent recharge push notification",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/send/push.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "to", value: '{"identity":["{user_id}"]}' },
              {
                key: "content",
                value:
                  '{"title":"⚡ Low Balance Alert","body":"{personalized_message} Your balance is ₹{current_balance}. Recharge ₹{suggested_recharge_amount} now to avoid interruption.","deeplink":"app://wallet/recharge?amount={suggested_recharge_amount}"}',
              },
            ],
            variables: [
              { name: "status", customName: "push_status", type: "string" },
            ],
          },
        },
      ),
      n("6", "delayAction", "Wait 24 hours for recharge", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "Wallet still below threshold?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "current_balance", operator: "less_than", value: "100" },
          ],
        },
      }),
      smsNode(
        "8",
        "SMS: Final recharge reminder with quick-pay link",
        1720,
        "{user_phone}",
        "Hi! Your wallet balance is low (₹{current_balance}). Recharge ₹{suggested_recharge_amount} instantly: https://app.link/recharge?amt={suggested_recharge_amount}. Avoid service interruptions — recharge now!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Monitors wallet balance via CleverTap events and sends a timely push notification when balance drops below the average transaction amount. If the user does not recharge within 24 hours, an SMS follow-up with a quick-pay deep link ensures uninterrupted service. Ideal for fintech apps, prepaid wallets, and digital payment platforms.",
  },

  // ── 215. Offer Burn / Coupon Reminder | CleverTap | Pattern D ──
  "Offer Burn / Coupon Reminder": {
    nodes: [
      trigger("CleverTap: Unused coupon approaching expiry", "webhook_app", {
        source: "clevertap",
        event: "coupon_expiring_soon",
        howItStarts:
          "Fires when a user has an unredeemed coupon that expires within 48 hours, detected via CleverTap scheduled campaign or event.",
        setup:
          "Connect CleverTap and configure the 'coupon_expiring_soon' event. Include user_id, coupon_code, discount_value, discount_type, expiry_date, and eligible_categories in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Analyze coupon details and personalize urgency",
        280,
        "You are a promotional engagement specialist. Analyze coupon details and craft a personalized redemption message. Return JSON with fields: user_id, coupon_code, discount_display (e.g. '20% off' or '₹500 off'), hours_until_expiry, eligible_categories_list, urgency_headline, redemption_cta.",
        "Coupon event: {webhook_payload}",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "coupon_code", as: "coupon_code", type: "string" },
          { field: "discount_display", as: "discount_display", type: "string" },
          {
            field: "hours_until_expiry",
            as: "hours_until_expiry",
            type: "number",
          },
          { field: "urgency_headline", as: "urgency_headline", type: "string" },
          { field: "redemption_cta", as: "redemption_cta", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "CleverTap: Verify coupon validity and user preferences",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profiles.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "identity", value: "{user_id}" },
              { key: "type", value: "profile" },
            ],
            variables: [
              {
                name: "record.profileData.push_opt_in",
                customName: "push_opted_in",
                type: "boolean",
              },
              {
                name: "record.profileData.preferred_category",
                customName: "preferred_category",
                type: "string",
              },
              {
                name: "record.profileData.last_purchase_date",
                customName: "last_purchase_date",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Coupon expires within 24 hours?", 760, {
        conditionDetails: {
          conditions: [
            { field: "hours_until_expiry", operator: "less_than", value: "24" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "CleverTap: Send urgent coupon push notification",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/send/push.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "to", value: '{"identity":["{user_id}"]}' },
              {
                key: "content",
                value:
                  '{"title":"{urgency_headline}","body":"Your {discount_display} coupon ({coupon_code}) expires in {hours_until_expiry}h! {redemption_cta}","deeplink":"app://shop?coupon={coupon_code}&category={preferred_category}"}',
              },
            ],
            variables: [
              {
                name: "status",
                customName: "push_sent_status",
                type: "string",
              },
            ],
          },
        },
      ),
      n("6", "delayAction", "Wait 12 hours", 1240, {
        delayDetails: { duration: 12, unit: "hours" },
      }),
      n("7", "condition", "Coupon still unredeemed?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "coupon_code", operator: "is_not_empty", value: "true" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Final coupon expiry warning with product picks",
        1720,
        "{user_email}",
        "⏰ Last chance: Your {discount_display} coupon expires today!",
        "<h2>Don't let your {discount_display} savings expire!</h2><p>Your coupon <strong>{coupon_code}</strong> is about to expire. Here are top picks in {preferred_category} you can save on:</p><p>Use code <strong>{coupon_code}</strong> at checkout before it's gone.</p><a href='https://shop.example.com?coupon={coupon_code}'>Shop Now & Save {discount_display}</a>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Tracks unredeemed coupons approaching expiry via CleverTap and sends a personalized push notification with the discount details and a deep link to relevant categories. If unused after 12 hours, a follow-up email with curated product recommendations drives last-chance redemption. Maximizes coupon burn rate and prevents wasted promotional spend.",
  },

  // ── 216. Send-Time Optimized Engagement Journey | CleverTap | Pattern I ──
  "Send-Time Optimized Engagement Journey": {
    nodes: [
      trigger(
        "CleverTap: User engagement trigger for optimal-time delivery",
        "webhook_app",
        {
          source: "clevertap",
          event: "engagement_content_ready",
          howItStarts:
            "Fires when a new engagement campaign (product update, content, feature tip) is ready and needs to be delivered at each user's optimal engagement time.",
          setup:
            "Connect CleverTap and configure the 'engagement_content_ready' event. Include user_id, content_type, content_title, content_body, deep_link, and campaign_id in the payload.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Extract content details and determine optimal send strategy",
        280,
        "You are a send-time optimization specialist. Analyze the engagement content and user context to determine the best delivery strategy. Return JSON with fields: user_id, content_type, content_title, content_summary, optimal_channel (push/email/in_app), personalization_angle, follow_up_content.",
        "Engagement content payload: {webhook_payload}",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "content_title", as: "content_title", type: "string" },
          { field: "optimal_channel", as: "optimal_channel", type: "string" },
          {
            field: "personalization_angle",
            as: "personalization_angle",
            type: "string",
          },
          {
            field: "follow_up_content",
            as: "follow_up_content",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "CleverTap: Fetch user's optimal engagement window",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profiles.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "identity", value: "{user_id}" },
              { key: "type", value: "profile" },
            ],
            variables: [
              {
                name: "record.profileData.optimal_send_hour",
                customName: "optimal_send_hour",
                type: "number",
              },
              {
                name: "record.profileData.timezone",
                customName: "user_timezone",
                type: "string",
              },
              {
                name: "record.profileData.engagement_score",
                customName: "engagement_score",
                type: "number",
              },
              {
                name: "record.profileData.preferred_channel",
                customName: "preferred_channel",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "User is highly engaged (score > 60)?", 760, {
        conditionDetails: {
          conditions: [
            {
              field: "engagement_score",
              operator: "greater_than",
              value: "60",
            },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "CleverTap: Send content at user's optimal time via push",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/send/push.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "to", value: '{"identity":["{user_id}"]}' },
              { key: "respect_frequency_caps", value: "true" },
              { key: "send_at_optimal_time", value: "true" },
              {
                key: "content",
                value:
                  '{"title":"{content_title}","body":"{personalization_angle}","deeplink":"{deep_link}"}',
              },
            ],
            variables: [
              { name: "status", customName: "send_status", type: "string" },
            ],
          },
        },
      ),
      n("6", "delayAction", "Wait 3 days for engagement", 1240, {
        delayDetails: { duration: 3, unit: "days" },
      }),
      n("7", "condition", "User interacted with content?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "send_status", operator: "equals", value: "success" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Follow-up with deeper content and next action",
        1720,
        "{user_email}",
        "More on {content_title} — curated for you",
        "<h2>{content_title}: Go Deeper</h2><p>{follow_up_content}</p><p>Based on your interests, we thought you'd love to explore more about this topic. Check out these related resources:</p><a href='{deep_link}'>Continue Exploring</a>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Leverages CleverTap's user engagement profiles to deliver content at each user's individually optimal time, boosting open and interaction rates. Highly engaged users receive push notifications timed to their peak activity window, while lower-engagement users get email. A 3-day follow-up with deeper content nurtures ongoing interest and drives sustained engagement.",
  },

  // ── 217. Dormant App User Reactivation | CleverTap | Pattern G ──
  "Dormant App User Reactivation": {
    nodes: [
      trigger("CleverTap: 45-day dormant user segment trigger", "schedule", {
        schedule: "Daily at 10:00 AM",
        howItStarts:
          "Runs daily to identify users who have not opened the app or engaged with any push/email in the last 45 days, pulling the dormant segment from CleverTap.",
        setup:
          "Create a CleverTap segment for users with last_app_open > 45 days ago and no push/email engagement. Schedule this workflow to run daily at 10 AM to process the dormant segment.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "CleverTap: Fetch dormant user segment with activity history",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profiles.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "event_name", value: "App Launched" },
              { key: "from", value: "{45_days_ago}" },
              { key: "to", value: "{today}" },
              { key: "type", value: "profile" },
            ],
            variables: [
              {
                name: "record.profileData.email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "record.profileData.name",
                customName: "user_name",
                type: "string",
              },
              {
                name: "record.profileData.last_app_open",
                customName: "last_app_open",
                type: "string",
              },
              {
                name: "record.profileData.lifetime_value",
                customName: "lifetime_value",
                type: "number",
              },
              {
                name: "record.profileData.favorite_feature",
                customName: "favorite_feature",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "AI: Analyze inactivity reason and craft reactivation message",
        520,
        "You are a mobile app reactivation specialist. Analyze the dormant user's history and craft a compelling return message. Return JSON with fields: likely_reason (feature_gap/competition/life_change/forgotten), reactivation_hook, subject_line, email_body_preview, whats_new_highlights.",
        "User: {user_name}, last seen: {last_app_open}, LTV: {lifetime_value}, favorite feature: {favorite_feature}",
        [
          { field: "likely_reason", as: "likely_reason", type: "string" },
          {
            field: "reactivation_hook",
            as: "reactivation_hook",
            type: "string",
          },
          { field: "subject_line", as: "subject_line", type: "string" },
          {
            field: "email_body_preview",
            as: "email_body_preview",
            type: "string",
          },
          {
            field: "whats_new_highlights",
            as: "whats_new_highlights",
            type: "string",
          },
        ],
      ),
      n("4", "condition", "High-value dormant user (LTV > $200)?", 760, {
        conditionDetails: {
          conditions: [
            { field: "lifetime_value", operator: "greater_than", value: "200" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Personalized reactivation with what's new",
        1000,
        "{user_email}",
        "{subject_line}",
        "<h2>We miss you, {user_name}!</h2><p>{reactivation_hook}</p><h3>Here's what's new since you've been away:</h3><p>{whats_new_highlights}</p><p>Your {favorite_feature} experience has gotten even better. Come back and see for yourself!</p><a href='https://app.example.com/welcome-back'>Open the App</a>",
      ),
      n("6", "delayAction", "Wait 10 days for re-engagement", 1240, {
        delayDetails: { duration: 10, unit: "days" },
      }),
      n("7", "condition", "User reopened the app?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "last_app_open",
              operator: "greater_than",
              value: "{reactivation_email_date}",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Stronger reactivation offer with exclusive incentive",
        1720,
        "{user_email}",
        "Exclusive for you: Come back and get 25% off your next purchase",
        "<h2>{user_name}, this one's just for you</h2><p>We noticed you haven't been back yet and we'd love to welcome you with something special.</p><p>Enjoy <strong>25% off</strong> your next purchase — our way of saying we'd love to have you back.</p><p>This offer expires in 7 days.</p><a href='https://app.example.com/welcome-back?offer=COMEBACK25'>Claim Your 25% Off</a>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Identifies users dormant for 45+ days via CleverTap segment analysis and sends AI-crafted reactivation emails highlighting new features relevant to each user's past behavior. High-value users receive priority treatment. After 10 days, users who haven't returned receive a stronger incentive offer to drive re-engagement and reduce permanent churn.",
  },

  // ── 218. High-Value User Concierge Journey | CleverTap | Pattern H ──
  "High-Value User Concierge Journey": {
    nodes: [
      trigger("CleverTap: User enters top 5% LTV segment", "webhook_app", {
        source: "clevertap",
        event: "user_entered_top_ltv_segment",
        howItStarts:
          "Fires when a user's lifetime value crosses into the top 5% threshold, qualifying them for concierge-level service via CleverTap segment triggers.",
        setup:
          "Connect CleverTap and set up a segment trigger for users crossing into the top 5% LTV bracket. Include user_id, total_ltv, total_orders, membership_duration, and most_purchased_category in the event payload.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "CleverTap: Fetch full VIP profile and loyalty data",
        280,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profiles.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "identity", value: "{user_id}" },
              { key: "type", value: "profile" },
            ],
            variables: [
              {
                name: "record.profileData.name",
                customName: "user_name",
                type: "string",
              },
              {
                name: "record.profileData.email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "record.profileData.phone",
                customName: "user_phone",
                type: "string",
              },
              {
                name: "record.profileData.total_ltv",
                customName: "total_ltv",
                type: "number",
              },
              {
                name: "record.profileData.loyalty_tier",
                customName: "current_tier",
                type: "string",
              },
              {
                name: "record.profileData.preferred_category",
                customName: "preferred_category",
                type: "string",
              },
            ],
          },
        },
      ),
      n("3", "condition", "Already in concierge program?", 520, {
        conditionDetails: {
          conditions: [
            { field: "current_tier", operator: "equals", value: "concierge" },
          ],
        },
      }),
      n(
        "4",
        "hitApi",
        "CleverTap: Send exclusive concierge welcome push",
        760,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/send/push.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "to", value: '{"identity":["{user_id}"]}' },
              {
                key: "content",
                value:
                  '{"title":"🎖️ Welcome to Concierge, {user_name}!","body":"You\'re in our top 5%. Enjoy priority support, exclusive access, and personalized recommendations. Tap to explore your new perks.","deeplink":"app://concierge/welcome"}',
              },
            ],
            variables: [
              { name: "status", customName: "push_status", type: "string" },
            ],
          },
        },
      ),
      n("5", "delayAction", "Wait 5 days for perks exploration", 1000, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("6", "condition", "User engaged with concierge perks?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "push_status", operator: "equals", value: "success" },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Concierge perks reminder with personal stylist intro",
        1480,
        "{user_email}",
        "{user_name}, your concierge perks are waiting",
        "<h2>Welcome to Concierge, {user_name}</h2><p>As one of our most valued customers (top 5%!), you now have access to:</p><ul><li><strong>Priority Support</strong> — Skip the queue with dedicated concierge chat</li><li><strong>Exclusive Access</strong> — Early drops and limited editions in {preferred_category}</li><li><strong>Personal Recommendations</strong> — Curated picks based on your taste</li><li><strong>Special Pricing</strong> — Members-only pricing on select items</li></ul><p>Your personal concierge is ready to assist. Reply to this email or tap below to get started.</p><a href='https://app.example.com/concierge'>Meet Your Concierge</a>",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log new concierge member",
        1720,
        "Concierge Members",
        '[["{user_name}","{user_email}","{total_ltv}","{current_tier}","{preferred_category}","{today}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_2"),
      e("3", "8", "output_1"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_2"),
      e("7", "8"),
    ],
    summary:
      "Automatically identifies users crossing into the top 5% LTV bracket via CleverTap and enrolls them in a concierge experience. New concierge members receive a push notification with exclusive perks, followed by a detailed email introducing priority support, early access, and personalized recommendations. Each enrollment is logged for CRM tracking and concierge team awareness.",
  },

  // ── 219. Omnichannel Checkout Rescue | CleverTap | Pattern B ──
  "Omnichannel Checkout Rescue": {
    nodes: [
      trigger("CleverTap: Checkout abandoned event", "webhook_app", {
        source: "clevertap",
        event: "checkout_abandoned",
        howItStarts:
          "Fires when a user initiates checkout but does not complete payment within 30 minutes, captured via CleverTap event tracking.",
        setup:
          "Connect CleverTap and configure the 'checkout_abandoned' event to fire 30 minutes after checkout initiation with no payment confirmation. Include user_id, cart_items, cart_total, payment_method_selected, and checkout_step_reached in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract checkout details and determine rescue strategy",
        280,
        "You are an e-commerce checkout recovery specialist. Analyze the abandoned checkout to determine the best rescue approach. Return JSON with fields: user_id, cart_total, item_count, top_item_name, checkout_step, likely_drop_reason (price_shock/distraction/payment_issue/comparison_shopping), rescue_message, urgency_level (high/medium/low).",
        "Checkout abandoned event: {webhook_payload}",
        [
          { field: "user_id", as: "user_id", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "top_item_name", as: "top_item_name", type: "string" },
          {
            field: "likely_drop_reason",
            as: "likely_drop_reason",
            type: "string",
          },
          { field: "rescue_message", as: "rescue_message", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "CleverTap: Lookup customer profile and channel preferences",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/profiles.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "identity", value: "{user_id}" },
              { key: "type", value: "profile" },
            ],
            variables: [
              {
                name: "record.profileData.email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "record.profileData.phone",
                customName: "user_phone",
                type: "string",
              },
              {
                name: "record.profileData.name",
                customName: "user_name",
                type: "string",
              },
              {
                name: "record.profileData.push_opt_in",
                customName: "push_opted_in",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Cart value > $75?", 760, {
        conditionDetails: {
          conditions: [
            { field: "cart_total", operator: "greater_than", value: "75" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "CleverTap: Send push notification — checkout rescue",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.clevertap.com/1/send/push.json",
            headers: [
              {
                key: "X-CleverTap-Account-Id",
                value: "{clevertap_account_id}",
              },
              { key: "X-CleverTap-Passcode", value: "{clevertap_passcode}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "to", value: '{"identity":["{user_id}"]}' },
              {
                key: "content",
                value:
                  '{"title":"Your {top_item_name} is waiting!","body":"{rescue_message} Complete your order now.","deeplink":"app://checkout/resume"}',
              },
            ],
            variables: [
              { name: "status", customName: "push_delivered", type: "string" },
            ],
          },
        },
      ),
      n("6", "delayAction", "Wait 4 hours", 1240, {
        delayDetails: { duration: 4, unit: "hours" },
      }),
      emailNode(
        "7",
        "Email: Checkout reminder with cart summary and social proof",
        1480,
        "{user_email}",
        "{user_name}, your cart with {top_item_name} is still waiting",
        "<h2>You're so close, {user_name}!</h2><p>You left {top_item_name} and your other items in your cart (total: ${cart_total}).</p><p>{rescue_message}</p><p>⭐ Over 2,000 customers purchased this item last month — don't miss out!</p><a href='https://shop.example.com/checkout/resume'>Complete Your Order</a>",
      ),
      smsNode(
        "8",
        "SMS: Final checkout rescue with limited-time incentive",
        1720,
        "{user_phone}",
        "Hi {user_name}! Your {top_item_name} cart (${cart_total}) is about to expire. Complete checkout now and get free shipping: https://shop.link/checkout — offer ends tonight!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "7", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Orchestrates a three-channel checkout rescue cascade via CleverTap: push notification fires first for immediate re-engagement, followed by a detailed email with cart summary and social proof after 4 hours, and finally an SMS with a free-shipping incentive as the final touchpoint. High-value carts receive push priority while lower-value carts skip directly to email, maximizing recovery while respecting channel fatigue.",
  },

  // ── 220. Welcome Canvas | Braze | Pattern A ──
  "Welcome Canvas": {
    nodes: [
      trigger("Braze: New user signup event", "webhook_app", {
        source: "braze",
        event: "user_signup_completed",
        howItStarts:
          "Fires when a new user completes registration, captured via Braze custom event or API trigger from the signup flow.",
        setup:
          "Connect Braze and configure the 'user_signup_completed' event trigger. Include external_id, email, first_name, signup_source, and opted_in_channels in the event payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract signup details and personalize welcome",
        280,
        "You are a customer onboarding specialist. Analyze the signup event and craft a personalized welcome strategy. Return JSON with fields: external_id, first_name, email, signup_source, welcome_message, recommended_first_action, value_proposition.",
        "Signup event: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "email", as: "user_email", type: "string" },
          { field: "signup_source", as: "signup_source", type: "string" },
          { field: "welcome_message", as: "welcome_message", type: "string" },
          {
            field: "recommended_first_action",
            as: "first_action",
            type: "string",
          },
          { field: "value_proposition", as: "value_prop", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Braze: Fetch user profile and subscription status",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://rest.iad-01.braze.com/users/export/ids",
            headers: [
              { key: "Authorization", value: "Bearer {braze_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "external_ids", value: '["{external_id}"]' },
              {
                key: "fields_to_export",
                value:
                  '["email","first_name","push_subscribe","email_subscribe","custom_attributes"]',
              },
            ],
            variables: [
              {
                name: "users[0].email_subscribe",
                customName: "email_subscribed",
                type: "string",
              },
              {
                name: "users[0].push_subscribe",
                customName: "push_subscribed",
                type: "string",
              },
              {
                name: "users[0].custom_attributes.account_type",
                customName: "account_type",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Existing customer returning?", 760, {
        conditionDetails: {
          conditions: [
            { field: "account_type", operator: "equals", value: "returning" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Cross-channel welcome with value prop and first action",
        1000,
        "{user_email}",
        "Welcome, {first_name}! Here's how to get started",
        "<h2>Welcome aboard, {first_name}! 🎉</h2><p>{welcome_message}</p><h3>Your first step:</h3><p>{first_action}</p><h3>Why you'll love it here:</h3><p>{value_prop}</p><p>Need help? Our team is here for you — just reply to this email.</p><a href='https://app.example.com/get-started'>Get Started Now</a>",
      ),
      n("6", "delayAction", "Wait 2 days for activation", 1240, {
        delayDetails: { duration: 2, unit: "days" },
      }),
      n("7", "condition", "User completed first key action?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "account_type",
              operator: "not_equals",
              value: "inactive",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Activation nudge with tips and success stories",
        1720,
        "{user_email}",
        "{first_name}, take the next step to unlock your experience",
        "<h2>Ready for more, {first_name}?</h2><p>We noticed you signed up but haven't completed {first_action} yet. Here's why it matters:</p><ul><li>Users who complete this step see 3x more value in their first week</li><li>It only takes 2 minutes</li><li>You'll unlock personalized recommendations immediately</li></ul><a href='https://app.example.com/get-started'>Complete Your Setup</a>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_2"),
      e("4", "5", "output_1"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Delivers a personalized cross-channel welcome experience via Braze when new users sign up. An AI-crafted welcome email highlights the value proposition and recommended first action. After 2 days, users who haven't activated receive a follow-up email with success stories and an activation nudge, driving first-action completion and early engagement.",
  },

  // ── 221. Activation Checklist Journey | Braze | Pattern A ──
  "Activation Checklist Journey": {
    nodes: [
      trigger("Braze: New user onboarding started", "webhook_app", {
        source: "braze",
        event: "onboarding_started",
        howItStarts:
          "Fires when a new user begins the onboarding flow after signup, triggering the activation checklist journey via Braze.",
        setup:
          "Connect Braze and configure the 'onboarding_started' event. Include external_id, email, first_name, plan_type, and checklist_items_total in the event payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Build personalized activation checklist and priorities",
        280,
        "You are a product activation specialist. Analyze the user's plan and context to prioritize their activation checklist. Return JSON with fields: external_id, first_name, email, plan_type, top_3_steps (array of step names), step_1_description, step_1_deeplink, motivational_message.",
        "Onboarding event: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "first_name", as: "first_name", type: "string" },
          { field: "email", as: "user_email", type: "string" },
          { field: "plan_type", as: "plan_type", type: "string" },
          {
            field: "step_1_description",
            as: "step_1_description",
            type: "string",
          },
          { field: "step_1_deeplink", as: "step_1_deeplink", type: "string" },
          {
            field: "motivational_message",
            as: "motivational_message",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Braze: Check activation progress and completed steps",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://rest.iad-01.braze.com/users/export/ids",
            headers: [
              { key: "Authorization", value: "Bearer {braze_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "external_ids", value: '["{external_id}"]' },
              {
                key: "fields_to_export",
                value: '["custom_attributes","custom_events"]',
              },
            ],
            variables: [
              {
                name: "users[0].custom_attributes.steps_completed",
                customName: "steps_completed",
                type: "number",
              },
              {
                name: "users[0].custom_attributes.checklist_total",
                customName: "checklist_total",
                type: "number",
              },
              {
                name: "users[0].custom_attributes.last_step_date",
                customName: "last_step_date",
                type: "string",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Completed at least 1 step?", 760, {
        conditionDetails: {
          conditions: [
            { field: "steps_completed", operator: "greater_than", value: "0" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Activation checklist with next step highlight",
        1000,
        "{user_email}",
        "{first_name}, your activation checklist — step {steps_completed} of {checklist_total} done!",
        "<h2>Great progress, {first_name}! 🚀</h2><p>{motivational_message}</p><p>You've completed <strong>{steps_completed} of {checklist_total}</strong> steps. Here's your next priority:</p><h3>Next Step: {step_1_description}</h3><p>This typically takes less than 3 minutes and unlocks key features for your {plan_type} plan.</p><a href='{step_1_deeplink}'>Complete This Step</a>",
      ),
      n("6", "delayAction", "Wait 48 hours for next milestone", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),
      n("7", "condition", "All checklist steps completed?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "steps_completed",
              operator: "equals",
              value: "{checklist_total}",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Activation complete celebration or next-step nudge",
        1720,
        "{user_email}",
        "{first_name}, you're almost there — finish your setup!",
        "<h2>{first_name}, don't stop now!</h2><p>You've completed {steps_completed} of {checklist_total} activation steps. Users who complete all steps get 5x more value from their {plan_type} plan.</p><p>Your next step: <strong>{step_1_description}</strong></p><p>It only takes a few minutes — let's finish this!</p><a href='{step_1_deeplink}'>Continue Setup</a>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_2"),
    ],
    summary:
      "Guides new Braze users through a personalized activation checklist, tracking milestone completion and sending targeted emails highlighting the next priority step. After 48 hours, users who haven't completed all steps receive a motivational nudge with progress stats and the value of finishing setup. Drives new-user activation rates and time-to-value.",
  },

  // ── 222. Abandoned Cart Canvas | Braze | Pattern B ──
  "Abandoned Cart Canvas": {
    nodes: [
      trigger("Braze: Cart abandoned event", "webhook_app", {
        source: "braze",
        event: "cart_abandoned",
        howItStarts:
          "Fires when a user adds items to cart but does not proceed to checkout within 1 hour, tracked via Braze custom event.",
        setup:
          "Connect Braze and configure the 'cart_abandoned' event to fire 1 hour after last cart activity with no checkout initiation. Include external_id, cart_items, cart_total, and cart_url in the event payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Analyze cart contents and craft recovery messaging",
        280,
        "You are an e-commerce cart recovery expert. Analyze the abandoned cart and create compelling recovery copy. Return JSON with fields: external_id, cart_total, item_count, hero_product_name, hero_product_image_url, social_proof_line, email_subject, push_title, in_app_cta.",
        "Cart abandoned event: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "hero_product_name", as: "hero_product", type: "string" },
          { field: "social_proof_line", as: "social_proof", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "push_title", as: "push_title", type: "string" },
          { field: "in_app_cta", as: "in_app_cta", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Braze: Fetch customer profile and purchase history",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://rest.iad-01.braze.com/users/export/ids",
            headers: [
              { key: "Authorization", value: "Bearer {braze_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "external_ids", value: '["{external_id}"]' },
              {
                key: "fields_to_export",
                value:
                  '["email","first_name","push_subscribe","custom_attributes"]',
              },
            ],
            variables: [
              {
                name: "users[0].email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "users[0].first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "users[0].push_subscribe",
                customName: "push_subscribed",
                type: "string",
              },
              {
                name: "users[0].custom_attributes.total_purchases",
                customName: "total_purchases",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Cart value > $100?", 760, {
        conditionDetails: {
          conditions: [
            { field: "cart_total", operator: "greater_than", value: "100" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Cart reminder with hero product and social proof",
        1000,
        "{user_email}",
        "{email_subject}",
        "<h2>{first_name}, you left something behind!</h2><p>Your <strong>{hero_product}</strong> and other items (${cart_total} total) are still in your cart.</p><p>{social_proof}</p><p>Complete your purchase before items sell out.</p><a href='https://shop.example.com/cart'>Return to Cart</a>",
      ),
      n("6", "delayAction", "Wait 6 hours", 1240, {
        delayDetails: { duration: 6, unit: "hours" },
      }),
      n("7", "condition", "Purchase still not completed?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "total_purchases",
              operator: "equals",
              value: "{total_purchases}",
            },
          ],
        },
      }),
      n(
        "8",
        "hitApi",
        "Braze: Send push notification with urgency angle",
        1720,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://rest.iad-01.braze.com/messages/send",
            headers: [
              { key: "Authorization", value: "Bearer {braze_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "external_user_ids", value: '["{external_id}"]' },
              {
                key: "messages",
                value:
                  '{"apple_push":{"alert":"{push_title}: Your {hero_product} cart (${cart_total}) won\'t last forever. Complete your order now!","extra":{"deeplink":"app://cart"}},"android_push":{"alert":"{push_title}: Your {hero_product} cart (${cart_total}) won\'t last forever. Complete your order now!","extra":{"deeplink":"app://cart"}}}',
              },
            ],
            variables: [
              {
                name: "dispatch_id",
                customName: "push_dispatch_id",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Executes a multi-channel cart recovery canvas through Braze: an email with hero product imagery and social proof sends first, followed by a push notification with urgency messaging after 6 hours. AI-crafted copy adapts to cart contents and value, while high-value carts receive prioritized treatment. Maximizes cart recovery through channel cascade.",
  },

  // ── 223. Browse Abandonment Canvas | Braze | Pattern C ──
  "Browse Abandonment Canvas": {
    nodes: [
      trigger("Braze: Product browsed without cart add", "webhook_app", {
        source: "braze",
        event: "browse_abandonment",
        howItStarts:
          "Fires when a user views 2+ products in the same category without adding any to cart within a session, tracked via Braze custom event.",
        setup:
          "Connect Braze and configure the 'browse_abandonment' event to fire at session end when 2+ product views occurred with no cart add. Include external_id, viewed_products, category, session_duration, and device_type in the event payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Extract browsed items and generate recommendations",
        280,
        "You are a product recommendation specialist. Analyze browsed products and generate personalized suggestions. Return JSON with fields: external_id, category, top_viewed_product, price_range, recommendation_reason, content_card_headline, email_subject, similar_products_summary.",
        "Browse abandonment event: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "category", as: "category", type: "string" },
          { field: "top_viewed_product", as: "top_product", type: "string" },
          { field: "price_range", as: "price_range", type: "string" },
          {
            field: "content_card_headline",
            as: "card_headline",
            type: "string",
          },
          { field: "email_subject", as: "email_subject", type: "string" },
          {
            field: "similar_products_summary",
            as: "similar_products",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Braze: Lookup customer affinity and browse history",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://rest.iad-01.braze.com/users/export/ids",
            headers: [
              { key: "Authorization", value: "Bearer {braze_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "external_ids", value: '["{external_id}"]' },
              {
                key: "fields_to_export",
                value: '["email","first_name","custom_attributes"]',
              },
            ],
            variables: [
              {
                name: "users[0].email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "users[0].first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "users[0].custom_attributes.browse_to_cart_rate",
                customName: "browse_cart_rate",
                type: "number",
              },
              {
                name: "users[0].custom_attributes.is_returning_customer",
                customName: "is_returning",
                type: "boolean",
              },
            ],
          },
        },
      ),
      n(
        "4",
        "condition",
        "Returning customer with high browse-to-cart rate?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "browse_cart_rate",
                operator: "greater_than",
                value: "0.15",
              },
            ],
          },
        },
      ),
      n(
        "5",
        "hitApi",
        "Braze: Create content card with browsed product highlight",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://rest.iad-01.braze.com/messages/send",
            headers: [
              { key: "Authorization", value: "Bearer {braze_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "external_user_ids", value: '["{external_id}"]' },
              {
                key: "messages",
                value:
                  '{"content_card":{"title":"{card_headline}","description":"Still thinking about {top_product}? Check out our {category} collection — starting at {price_range}.","url":"https://shop.example.com/category/{category}","extras":{"product":"{top_product}","category":"{category}"}}}',
              },
            ],
            variables: [
              {
                name: "dispatch_id",
                customName: "card_dispatch_id",
                type: "string",
              },
            ],
          },
        },
      ),
      n("6", "delayAction", "Wait 24 hours", 1240, {
        delayDetails: { duration: 24, unit: "hours" },
      }),
      n("7", "condition", "User still has no cart add?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "browse_cart_rate", operator: "less_than", value: "0.5" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Curated recommendations from browsed category",
        1720,
        "{user_email}",
        "{email_subject}",
        "<h2>{first_name}, still exploring {category}?</h2><p>We noticed you were checking out {top_product} and similar items. Here are our top picks curated just for you:</p><p>{similar_products}</p><p>Prices start at {price_range} — find your perfect match today.</p><a href='https://shop.example.com/category/{category}?rec=browse'>View Your Recommendations</a>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Captures browse abandonment via Braze and delivers a content card immediately highlighting the viewed product and category. After 24 hours without a cart add, a curated email with AI-generated product recommendations from the browsed category encourages conversion. Returning customers with high browse-to-cart rates receive priority engagement to capitalize on purchase intent.",
  },

  // ── 224. Checkout Started Rescue | Braze | Pattern B ──
  "Checkout Started Rescue": {
    nodes: [
      trigger("Braze: Checkout started but not completed", "webhook_app", {
        source: "braze",
        event: "checkout_started_abandoned",
        howItStarts:
          "Fires when a user initiates the checkout process (enters payment/shipping step) but does not complete the purchase within 45 minutes, tracked via Braze.",
        setup:
          "Connect Braze and configure the 'checkout_started_abandoned' event to fire 45 minutes after checkout initiation with no order confirmation. Include external_id, cart_items, cart_total, checkout_step_reached, and shipping_address_entered in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "AI: Analyze checkout drop-off point and craft rescue copy",
        280,
        "You are a checkout optimization specialist. Analyze where the user dropped off and create targeted rescue messaging. Return JSON with fields: external_id, cart_total, item_count, hero_item, checkout_step (shipping/payment/review), drop_off_reason (shipping_cost/payment_issue/price_comparison/distraction), rescue_email_subject, rescue_push_body, trust_signal.",
        "Checkout abandoned event: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "cart_total", as: "cart_total", type: "number" },
          { field: "hero_item", as: "hero_item", type: "string" },
          { field: "checkout_step", as: "checkout_step", type: "string" },
          { field: "drop_off_reason", as: "drop_off_reason", type: "string" },
          {
            field: "rescue_email_subject",
            as: "rescue_subject",
            type: "string",
          },
          { field: "trust_signal", as: "trust_signal", type: "string" },
        ],
      ),
      n("3", "hitApi", "Braze: Fetch customer details and order history", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://rest.iad-01.braze.com/users/export/ids",
          headers: [
            { key: "Authorization", value: "Bearer {braze_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "external_ids", value: '["{external_id}"]' },
            {
              key: "fields_to_export",
              value:
                '["email","first_name","phone","push_subscribe","custom_attributes"]',
            },
          ],
          variables: [
            {
              name: "users[0].email",
              customName: "user_email",
              type: "string",
            },
            {
              name: "users[0].first_name",
              customName: "first_name",
              type: "string",
            },
            {
              name: "users[0].phone",
              customName: "user_phone",
              type: "string",
            },
            {
              name: "users[0].custom_attributes.lifetime_orders",
              customName: "lifetime_orders",
              type: "number",
            },
          ],
        },
      }),
      n("4", "condition", "Dropped at payment step?", 760, {
        conditionDetails: {
          conditions: [
            { field: "checkout_step", operator: "equals", value: "payment" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Checkout rescue with trust signals and support offer",
        1000,
        "{user_email}",
        "{rescue_subject}",
        "<h2>{first_name}, your order is almost complete!</h2><p>You were just one step away from getting your <strong>{hero_item}</strong> (${cart_total}).</p><p>{trust_signal}</p><p>Having trouble with checkout? Our support team can help — just reply to this email or call us at 1-800-HELP.</p><a href='https://shop.example.com/checkout/resume'>Complete Your Order</a><p style='color:#666;font-size:12px'>🔒 256-bit SSL encryption | Free returns within 30 days | 24/7 support</p>",
      ),
      n("6", "delayAction", "Wait 8 hours", 1240, {
        delayDetails: { duration: 8, unit: "hours" },
      }),
      n("7", "condition", "Order still not placed?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "lifetime_orders",
              operator: "equals",
              value: "{lifetime_orders}",
            },
          ],
        },
      }),
      smsNode(
        "8",
        "SMS: Final checkout rescue with direct cart link",
        1720,
        "{user_phone}",
        "Hi {first_name}! Your {hero_item} order (${cart_total}) is saved and ready. Complete checkout here: https://shop.link/checkout-resume — Need help? Reply HELP.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Rescues high-intent checkout abandoners through Braze with a targeted channel cascade. AI analyzes the exact checkout step where the user dropped off to craft contextual messaging — payment-step drop-offs receive trust signals and support offers, while shipping-step drop-offs highlight delivery benefits. An SMS follow-up after 8 hours provides a direct cart link as the final rescue touchpoint.",
  },

  // ── 225. Price Drop Journey | Braze | Pattern D ──
  "Price Drop Journey": {
    nodes: [
      trigger(
        "Braze: Price drop on wishlisted or viewed product",
        "webhook_app",
        {
          source: "braze",
          event: "product_price_dropped",
          howItStarts:
            "Fires when a product that a user has wishlisted or recently viewed drops in price by 10% or more, detected via catalog sync with Braze.",
          setup:
            "Connect Braze and configure the 'product_price_dropped' event via catalog webhook. Include external_id, product_id, product_name, original_price, new_price, discount_percentage, product_url, and image_url in the payload.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "AI: Analyze price drop and craft alert messaging",
        280,
        "You are a pricing alert specialist. Analyze the price drop details and create compelling alert copy. Return JSON with fields: external_id, product_name, original_price, new_price, savings_amount, discount_pct, urgency_level (high/medium/low), push_headline, email_subject, scarcity_message.",
        "Price drop event: {webhook_payload}",
        [
          { field: "external_id", as: "external_id", type: "string" },
          { field: "product_name", as: "product_name", type: "string" },
          { field: "original_price", as: "original_price", type: "number" },
          { field: "new_price", as: "new_price", type: "number" },
          { field: "savings_amount", as: "savings_amount", type: "number" },
          { field: "discount_pct", as: "discount_pct", type: "number" },
          { field: "push_headline", as: "push_headline", type: "string" },
          { field: "email_subject", as: "email_subject", type: "string" },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Braze: Verify user interest and notification preferences",
        520,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://rest.iad-01.braze.com/users/export/ids",
            headers: [
              { key: "Authorization", value: "Bearer {braze_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "external_ids", value: '["{external_id}"]' },
              {
                key: "fields_to_export",
                value:
                  '["email","first_name","push_subscribe","custom_attributes"]',
              },
            ],
            variables: [
              {
                name: "users[0].email",
                customName: "user_email",
                type: "string",
              },
              {
                name: "users[0].first_name",
                customName: "first_name",
                type: "string",
              },
              {
                name: "users[0].push_subscribe",
                customName: "push_subscribed",
                type: "string",
              },
              {
                name: "users[0].custom_attributes.wishlist_count",
                customName: "wishlist_count",
                type: "number",
              },
            ],
          },
        },
      ),
      n("4", "condition", "Discount > 20%?", 760, {
        conditionDetails: {
          conditions: [
            { field: "discount_pct", operator: "greater_than", value: "20" },
          ],
        },
      }),
      n(
        "5",
        "hitApi",
        "Braze: Send push notification with price drop alert",
        1000,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://rest.iad-01.braze.com/messages/send",
            headers: [
              { key: "Authorization", value: "Bearer {braze_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "external_user_ids", value: '["{external_id}"]' },
              {
                key: "messages",
                value:
                  '{"apple_push":{"alert":"{push_headline}: {product_name} is now ${new_price} (was ${original_price}). Save ${savings_amount}!","extra":{"deeplink":"app://product/{product_id}"}},"android_push":{"alert":"{push_headline}: {product_name} is now ${new_price} (was ${original_price}). Save ${savings_amount}!","extra":{"deeplink":"app://product/{product_id}"}}}',
              },
            ],
            variables: [
              {
                name: "dispatch_id",
                customName: "push_dispatch_id",
                type: "string",
              },
            ],
          },
        },
      ),
      n("6", "delayAction", "Wait 36 hours", 1240, {
        delayDetails: { duration: 36, unit: "hours" },
      }),
      n("7", "condition", "Product still available and not purchased?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "wishlist_count", operator: "greater_than", value: "0" },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Price drop follow-up with urgency and similar deals",
        1720,
        "{user_email}",
        "{email_subject}",
        "<h2>{first_name}, {product_name} is still on sale!</h2><p><strong>Was:</strong> <s>${original_price}</s> <strong>Now:</strong> ${new_price} — you save ${savings_amount} ({discount_pct}% off)</p><p>This price won't last forever. Grab it before it goes back up or sells out.</p><h3>You might also like:</h3><p>Check out similar items on sale in your wishlist.</p><a href='https://shop.example.com/product/{product_id}'>Buy Now at ${new_price}</a>",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Monitors price drops on wishlisted and recently viewed products via Braze and sends immediate push alerts with savings details and deep links. Significant discounts (>20%) trigger push first for urgency, while moderate drops go directly to email. After 36 hours, a follow-up email with urgency messaging and similar deals drives conversion for users who haven't yet purchased.",
  },
};
